import { Navigate } from 'react-router-dom';
import { useAuth, useFeatureFlags } from '@smarty/shared';
import { storePath } from '@/config/routes.config';
import { Page, Box, InlineStack, Spinner } from '@/lib/polaris';

/**
 * Portón para pantallas que sólo tienen sentido en una TIENDA, no en una cuenta de reventa.
 *
 * 🪤 ESTO PREGUNTABA POR `ecommerce`, Y ERA LA SEÑAL EQUIVOCADA. El Inicio usaba
 *    `<FeatureProtectedRoute feature="ecommerce" fallback={BIENVENIDA_DROP}>` con un comentario que
 *    decía «para Básico, Pro y Enterprise esto es un no-op: las tres lo incluyen». Era verdad
 *    cuando se escribió y dejó de serlo sin que nadie tocara esa línea: `ecommerce` pasó a
 *    `development`, y el filtro de madurez lo apaga para toda cuenta de cliente. Resultado: un
 *    cliente en plan Básico entraba al panel y lo rebotaba a la bienvenida de Smarty Drop — el
 *    menú le ofrecía «Inicio» y la ruta lo mandaba a otro lado.
 *
 * ⭐ LA REGLA ES DEL PLAN, NO DE UN MÓDULO. `FeatureFlagsContext` expone `planType` justamente para
 *    esto, y su comentario ya avisaba: «si la UI usara otra señal para decidir qué esconder (por
 *    ejemplo la ausencia de `ecommerce`), habría dos definiciones de la misma regla y el día que
 *    una cambie la pantalla ofrecería algo que la base rechaza». Es exactamente lo que pasó.
 *
 * 🪤 Y LA DIFERENCIA IMPORTA MÁS DE LO QUE PARECE: un módulo apagado por MADUREZ vuelve a
 *    encenderse solo cuando el módulo se termina, mientras que «esta cuenta es de reventa» no
 *    cambia nunca por eso. Atar la segunda regla a la primera hace que terminar un módulo mueva,
 *    de rebote, quién puede entrar a otra pantalla.
 */
export function RutaSoloParaTiendas({
  children,
  fallback,
}: {
  children: React.ReactNode;
  /** Ruta a la que se manda una cuenta de reventa. Se le antepone `/store/:slug` cuando aplica. */
  fallback: string;
}) {
  const { storeSlug } = useAuth();
  const { planType, loading } = useFeatureFlags();

  if (loading) {
    return (
      <Page>
        <Box padding="800">
          <InlineStack align="center">
            <Spinner size="large" />
          </InlineStack>
        </Box>
      </Page>
    );
  }

  /*
   * 🪤 EL PLAN NO SE FINGE EN DESARROLLO. `FeatureProtectedRoute` enciende todos los módulos en dev,
   *    pero esto es el ROL del negocio, que es un dato: un dropshipper en localhost tiene que ver lo
   *    que va a ver en producción, o lo del rol no se puede revisar antes de publicarlo.
   */
  if (planType === 'drop') {
    return <Navigate to={storeSlug ? storePath(storeSlug, fallback) : fallback} replace />;
  }

  return <>{children}</>;
}
